import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Ton pigułki. `neutral` to wartość bez własnego znaczenia — nazwa opcji,
 * kategoria, licznik; reszta niesie barwę, gdy pozycja ma wymowę.
 */
export type ChipTone =
  | 'neutral'
  | 'accent'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

/**
 * `outline` to domyślna forma: barwa na ramce i napisie, tło białe. `soft`
 * dokłada bladą wypełń tam, gdzie pigułka ma zaważyć — pojedyncze wyróżnienie
 * w nagłówku rekordu albo ostrzeżenie w formularzu rezerwacji.
 */
export type ChipFill = 'outline' | 'soft';

const TONE_CLASS: Record<ChipTone, Record<ChipFill, string>> = {
  neutral: {
    outline: 'border-neutral-200 text-neutral-700',
    soft: 'border-transparent bg-neutral-100 text-neutral-700',
  },
  accent: {
    outline: 'border-accent-border text-accent-900',
    soft: 'border-transparent bg-accent-200 text-accent-900',
  },
  info: {
    outline: 'border-info-100 text-info-300',
    soft: 'border-transparent bg-info-100 text-info-300',
  },
  success: {
    outline: 'border-success-100 text-success-300',
    soft: 'border-transparent bg-success-100 text-success-300',
  },
  warning: {
    outline: 'border-accent-border text-accent-800',
    soft: 'border-transparent bg-accent-200 text-accent-800',
  },
  error: {
    outline: 'border-error-100 text-error-300',
    soft: 'border-transparent bg-error-100 text-error-300',
  },
};

const SIZE_CLASS = {
  /** Komórka tabeli — pigułka nie może podnosić wiersza. */
  sm: 'px-1.5 py-0.5 text-[11px]',
  /** Wszystko poza tabelą. */
  md: 'px-2 py-0.5 text-xs',
} as const;

interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Napis pigułki; ten sam trafia do `title`, bo ucięty bywa nierozpoznawalny. */
  label: string;
  tone?: ChipTone;
  fill?: ChipFill;
  size?: keyof typeof SIZE_CLASS;
  /**
   * Licznik przy napisie — ile osób wybrało ten wariant, ile pozycji ma
   * kategoria. Stoi poza `truncate`, żeby ucinał się napis, nie liczba.
   */
  count?: number;
  /** Pozycja zdjęta ze szkicu — ramka przerywana i przekreślenie. */
  struck?: boolean;
  onRemove?: () => void;
}

/**
 * Pigułka pojedynczej wartości — tag, wybrana opcja, kategoria, atrybut
 * rekordu. Stan rekordu pigułką nie jest: od tego jest `StatusDot`.
 *
 * `min-w-0` jest obowiązkowe: element elastyczny ma `min-width: auto`, które
 * wygrywa z `max-width`, więc jedno długie słowo rozepchnęłoby pigułkę.
 */
const Chip = ({
  label,
  tone = 'neutral',
  fill = 'outline',
  size = 'md',
  count,
  struck = false,
  onRemove,
  className,
  ...props
}: ChipProps) => (
  <span
    title={label}
    className={cn(
      'inline-flex w-fit min-w-0 max-w-[180px] items-center gap-1 rounded-full border bg-basic-white align-middle font-medium leading-none',
      SIZE_CLASS[size],
      TONE_CLASS[tone][fill],
      struck && 'border-dashed text-neutral-400 line-through',
      className,
    )}
    {...props}
  >
    <span className="truncate">{label}</span>
    {count !== undefined && (
      <span className="shrink-0 tabular-nums opacity-60">{count}</span>
    )}
    {onRemove && (
      <button
        type="button"
        aria-label={`Usuń ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        // Krzyżyk nie może budzić kontenera, a react-select reaguje już na mousedown.
        onMouseDown={(event) => event.stopPropagation()}
        className="-mr-0.5 shrink-0 rounded-full transition-opacity hover:opacity-60"
      >
        <X className="size-3" />
      </button>
    )}
  </span>
);

export default Chip;
